import { useState } from 'react'
import { ChevronDown, ArrowRight } from 'lucide-react'
import { usePublicStats } from '@/hooks/usePublicStats'
import { TELEGRAM_BOT_URL } from '@/lib/constants'
import { cn } from '@/lib/utils'
import { useScrollReveal } from '@/hooks/useScrollReveal'

function useFaqs() {
  const { data } = usePublicStats()
  const trialDays = data?.free_trial_days ?? 5

  return [
    {
      q: '¿Qué es John Anthony?',
      a: 'Un sistema de inteligencia artificial que analiza partidos de fútbol y publica recomendaciones de apuestas, junto con su rendimiento real, dentro de una Mini App de Telegram.',
    },
    {
      q: '¿Por qué usar IA y no tipsters?',
      a: 'Los tipsters toman decisiones basadas en intuición, experiencia personal y, a veces, en intereses propios. John Anthony no tiene emociones: cada pick pasa por modelos estadísticos, control de riesgo y umbrales mínimos de calidad objetivos. El historial es auditable y público.',
    },
    {
      q: '¿Cada cuánto se actualizan los picks?',
      a: 'El sistema analiza los partidos disponibles cada madrugada y publica los picks del día cada mañana. No hay picks en vivo ni actualizaciones durante el día — solo Prematch para garantizar la calidad del análisis.',
    },
    {
      q: '¿Los resultados que muestran son reales?',
      a: 'Sí. El win rate y el ROI de la sección "Resultados" se calculan solo sobre picks ya liquidados (ganados o perdidos) — nunca se estima ni se maquilla un número.',
    },
    {
      q: '¿Qué ocurre si un pick pierde?',
      a: 'Queda registrado públicamente. El sistema analiza el pick en el módulo Post-Mortem para entender qué falló y ajustar los modelos. La transparencia incluye las pérdidas, no solo las victorias.',
    },
    {
      q: '¿Cómo calculan el ROI?',
      a: 'ROI = (ganancia neta total / stake total apostado). Asumimos stake unitario de 1 en cada pick para que la comparativa sea justa e independiente del tamaño de las apuestas. Un ROI del 100% significa que por cada €100 apostados, se han recuperado €200.',
    },
    {
      q: '¿Qué ligas analizan?',
      a: 'El sistema cubre más de 20 ligas internacionales incluyendo Premier League, LaLiga, Bundesliga, Serie A, Ligue 1, y competiciones de América Latina, Europa del Este y Asia. El foco siempre es calidad del dato sobre cantidad de picks.',
    },
    {
      q: `¿Cómo funciona la prueba gratis de ${trialDays} días?`,
      a: `Al abrir la Mini App por primera vez desde Telegram, tu cuenta queda activa con el plan Free automáticamente. ${trialDays} días de acceso sin tarjeta ni compromiso.`,
    },
    {
      q: '¿Necesito instalar algo?',
      a: 'No. John Anthony funciona completamente dentro de Telegram como una Mini App. Solo necesitas tener Telegram instalado en tu móvil u ordenador — no hay descarga adicional.',
    },
    {
      q: '¿Cómo pago los planes de pago?',
      a: 'Con tarjeta a través de Stripe (activación instantánea y renovación automática) o por Bizum si prefieres pagar manualmente cada mes.',
    },
    {
      q: '¿Puedo cancelar cuando quiera?',
      a: 'Sí, desde "Mi Suscripción" dentro de la app puedes gestionar tu método de pago o cancelar la renovación automática en cualquier momento. Sin penalizaciones.',
    },
  ]
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="card reveal transition-all">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 text-left"
      >
        <span className="font-semibold text-sm sm:text-base">{q}</span>
        <ChevronDown
          className={cn('h-4 w-4 text-text-dim shrink-0 transition-transform duration-200', open && 'rotate-180')}
        />
      </button>
      {open && (
        <p className="mt-3 text-sm text-text-dim leading-relaxed border-t border-border pt-3">
          {a}
        </p>
      )}
    </div>
  )
}

export function FaqSection() {
  const faqs = useFaqs()
  const ref = useScrollReveal<HTMLElement>()

  return (
    <section ref={ref} className="max-w-3xl mx-auto px-5 py-20">
      <div className="text-center mb-10 reveal">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand mb-2">
          Preguntas frecuentes
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold">Antes de empezar</h2>
        <p className="mt-3 text-text-dim text-sm">
          Respuestas honestas a las dudas más comunes.
        </p>
      </div>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <div key={f.q} style={{ transitionDelay: `${i * 40}ms` }}>
            <FaqItem q={f.q} a={f.a} />
          </div>
        ))}
      </div>

      <div className="cta-strip reveal" style={{ transitionDelay: '200ms' }}>
        <a href={TELEGRAM_BOT_URL} target="_blank" rel="noopener noreferrer" className="btn-brand">
          ¿Convencido? Empezar gratis
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  )
}
